import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import {
  Check,
  ChevronLeft,
  Edit3,
  FileUp,
  Info,
  Loader2,
  Plus,
  Trash2,
  X
} from 'lucide-react'
import styles from './ProjectAutofillCard.module.scss'
import sm from '../../../../UI/NewSubProjectManager/NewSubProjectManagerV2.module.scss'
import { patchProjectAutofillDraft } from '../../../../../services/projectAutofillDraftService.ts'
import { getPrivateProjectById } from '../../../../../store/actions'
import { useProjectAutofillDraftGuardOptional } from '../../../../../contexts/ProjectAutofillDraftGuardContext.tsx'
import { useActionGuard } from '../../../../../hooks/useActionGuard.ts'
import { profesionalesService } from '../../../../../services/profesionalesService.ts'
import PlacesAutocomplete from '../../../../UI/PlacesAutocomplete/PlacesAutocomplete.tsx'
import ModalOverlay from '../../../../UI/ModalOverlay/ModalOverlay.tsx'
import DocumentInsightPanel from '../DocumentInsightPanel/DocumentInsightPanel.tsx'
import DocumentInsightProceedWarnModal from '../DocumentInsightProceedWarnModal.tsx'
import BudgetSheetFullscreenModal from './BudgetSheetFullscreenModal.tsx'
import ProjectAutofillDefaultRubroCard from './ProjectAutofillDefaultRubroCard.tsx'
import ProjectAutofillRubroDetailBody from './ProjectAutofillRubroDetailBody.tsx'
import ProjectAutofillSaveFooter from './ProjectAutofillSaveFooter.tsx'
import type {
  CommittedDraftSnapshot,
  DraftMetadataFileEntry,
  DraftValidationShape,
  ProjectAutofillPayload,
  RubroRow,
  SkillOption
} from '../../../../../utils/conversationalMode/projectAutofillDraftUtils.ts'
import {
  clampIsoDateToMax,
  draftMetadataFileLabel,
  EMPTY_REQUIRED_FIELDS,
  getEffectiveDocumentationPageCount,
  humanizeDraftStatus,
  mapRubrosToDraftPayload,
  rubroHasValidSelectedSkill,
  isRubroFallbackOtrosKind,
  normalizeDocumentationFileIds,
  normalizeDocumentationSlots,
  getRubroTempIdsWithDocumentationPageIssues,
  rubroNeedsMasterPdfForCrop,
  serializeRubrosSnapshot,
  shallowMergeDraftMetadataForPatch,
  validateRubrosDocumentationPages
} from '../../../../../utils/conversationalMode/projectAutofillDraftUtils.ts'
import {
  computeProjectAutofillEditorUiState,
  matchValidationMessagesToRubroTempIds,
  mergeProjectAutofillDraftValidation,
  warningMatchesSuggestedDescription,
  warningMatchesSuggestedDocumentation,
  warningMatchesSuggestedEndDate,
  warningMatchesSuggestedName
} from '../../../../../utils/conversationalMode/projectAutofillEditorValidation.ts'
import {
  resolveBudgetSheetReadonlyReason,
  rubroBudgetSlotHasFileOrPages
} from '../../../../../utils/conversationalMode/budgetSheetStateUtils.ts'
import {
  isRubroFuzzyKind,
  isRubroUnresolvedKind,
  PENDING_INPUT_STYLE,
  PUBLISHED_RUBRO_EDIT_HINT,
  rubroFallbackOtrosReview,
  rubroHeadline,
  rubroKey,
  rubroPrimaryDisplayName,
  rubroUnresolvedNeedsPick
} from './projectAutofillCard/projectAutofillCardUtils.ts'
import { useProjectAutofillPayloadHydration } from './projectAutofillCard/hooks/useProjectAutofillPayloadHydration.ts'
import { useProjectAutofillRubroDocumentation } from './projectAutofillCard/hooks/useProjectAutofillRubroDocumentation.ts'
import { useProjectAutofillRubroInteractions } from './projectAutofillCard/hooks/useProjectAutofillRubroInteractions.ts'
import { useProjectAutofillSavePublish } from './projectAutofillCard/hooks/useProjectAutofillSavePublish.ts'

const ProjectAutofillCard: React.FC<{
  payload: ProjectAutofillPayload
  embedLayout?: 'default' | 'createObraDesktop'
  onRequestChangeSourceDocument?: () => void
}> = ({ payload, embedLayout = 'default', onRequestChangeSourceDocument }) => {
  const isCreateObraShell = embedLayout === 'createObraDesktop'
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const createObraPublishRedirectRef = useRef(false)
  const draftGuardCtx = useProjectAutofillDraftGuardOptional()
  const guardSetApi = draftGuardCtx?.setProjectAutofillDraftGuardApi
  const guardBumpUi = draftGuardCtx?.bumpDraftUi
  const { checkAccess } = useActionGuard()
  const edifyUserId = useSelector((state: { general?: { userData?: { id?: string | number } } }) =>
    state.general?.userData?.id != null ? String(state.general.userData.id) : ''
  )
  const cachedSkills = useSelector((state: any) => state.register?.skillsList || [])
  const cachedSkillsRef = useRef(cachedSkills)
  cachedSkillsRef.current = cachedSkills
  const initialProject = payload.draft?.project || {}
  const [name, setName] = useState(initialProject.name || '')
  const [description, setDescription] = useState(initialProject.description || '')
  const [zone, setZone] = useState(initialProject.zone || '')
  const [startDate, setStartDate] = useState(initialProject.startDate || '')
  const [endDate, setEndDate] = useState(initialProject.endDate || '')
  const [rubros, setRubros] = useState<RubroRow[]>([])
  const [expandedRubros, setExpandedRubros] = useState<Record<string, boolean>>({})
  const [skillOptions, setSkillOptions] = useState<SkillOption[]>([])
  const [fuzzyConfirmByRubro, setFuzzyConfirmByRubro] = useState<Record<string, boolean>>({})
  const [patchedDraftSync, setPatchedDraftSync] = useState<{
    validation?: DraftValidationShape
    collectedData?: ProjectAutofillPayload['collectedData']
  } | null>(null)
  const [metadataFilesOverride, setMetadataFilesOverride] = useState<DraftMetadataFileEntry[] | null>(null)
  const [uploadingSlotKey, setUploadingSlotKey] = useState<string | null>(null)
  const [pendingRubroSkillChange, setPendingRubroSkillChange] = useState<{
    tempId: string
    skill: SkillOption
  } | null>(null)
  const [createObraRubroModalRid, setCreateObraRubroModalRid] = useState<string | null>(null)
  const [budgetSheetFullscreenTempId, setBudgetSheetFullscreenTempId] = useState<string | null>(null)
  const [createObraPdfInfoOpen, setCreateObraPdfInfoOpen] = useState(false)
  const createObraPdfInfoRef = useRef<HTMLDivElement>(null)
  const [draftFilesListOpen, setDraftFilesListOpen] = useState(false)
  const draftFilesListRef = useRef<HTMLDivElement>(null)
  const committedRef = useRef<CommittedDraftSnapshot | null>(null)
  const [dismissedDocumentInsight, setDismissedDocumentInsight] = useState(false)
  const [insightBannerDismissWarnOpen, setInsightBannerDismissWarnOpen] = useState(false)

  useEffect(() => {
    setDismissedDocumentInsight(false)
  }, [
    payload.draftId,
    payload.draft?.id,
    payload.documentInsight?.kind,
    payload.documentInsight?.titleForUser
  ])

  useEffect(() => {
    if (!createObraPdfInfoOpen) return
    const onDocMouseDown = (e: MouseEvent) => {
      if (createObraPdfInfoRef.current && !createObraPdfInfoRef.current.contains(e.target as Node)) {
        setCreateObraPdfInfoOpen(false)
      }
    }
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setCreateObraPdfInfoOpen(false)
    }
    document.addEventListener('mousedown', onDocMouseDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [createObraPdfInfoOpen])

  useEffect(() => {
    if (!draftFilesListOpen) return
    const onDocMouseDown = (e: MouseEvent) => {
      if (draftFilesListRef.current && !draftFilesListRef.current.contains(e.target as Node)) {
        setDraftFilesListOpen(false)
      }
    }
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setDraftFilesListOpen(false)
    }
    document.addEventListener('mousedown', onDocMouseDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [draftFilesListOpen])

  useEffect(() => {
    let mounted = true
    const mapReduxSkills = (list: unknown[]): SkillOption[] =>
      (list || [])
        .map((skill: any) => ({
          id: Number(skill.id),
          name: String(skill.name || '').trim()
        }))
        .filter((skill: SkillOption) => !!skill.id && !!skill.name)
    const load = async () => {
      try {
        const skills = await profesionalesService.fetchSkillCatalog()
        if (!mounted) return
        setSkillOptions(skills)
      } catch {
        if (!mounted) return
        setSkillOptions(mapReduxSkills(cachedSkillsRef.current || []))
      }
    }
    void load()
    return () => {
      mounted = false
    }
  }, [])

  const draftId = payload.draftId || payload.collectedData?.draftId || payload.draft?.id

  useEffect(() => {
    setMetadataFilesOverride(null)
  }, [draftId])

  const serverMetadataFiles = useMemo((): DraftMetadataFileEntry[] => {
    const raw = payload.draft?.metadata?.files
    return Array.isArray(raw) ? (raw as DraftMetadataFileEntry[]) : []
  }, [payload.draft?.metadata?.files])

  const metadataFiles = metadataFilesOverride ?? serverMetadataFiles

  const metadataFilesWithUrl = useMemo(
    () => metadataFiles.filter((f) => String(f.storageUrl || '').trim()),
    [metadataFiles]
  )

  const patchMetadataPayload = useCallback(() => {
    const m = shallowMergeDraftMetadataForPatch(payload.draft?.metadata)
    const effective = metadataFilesOverride ?? serverMetadataFiles
    if (effective.length > 0) {
      m.files = effective
    }
    return m
  }, [payload.draft?.metadata, metadataFilesOverride, serverMetadataFiles])

  const mergedCollectedData =
    patchedDraftSync?.collectedData != null
      ? { ...payload.collectedData, ...patchedDraftSync.collectedData }
      : payload.collectedData
  const requiredFields = mergedCollectedData?.requiredFields ?? EMPTY_REQUIRED_FIELDS
  const requiredFieldsWithZone = useMemo(
    () => Array.from(new Set([...requiredFields, 'project.zone'])),
    [requiredFields]
  )
  const mergedDraftValidation = useMemo(
    () =>
      mergeProjectAutofillDraftValidation({
        patched: patchedDraftSync?.validation,
        payload: payload.validation,
        draft: payload.draft?.validation
      }),
    [patchedDraftSync?.validation, payload.validation, payload.draft?.validation]
  )
  const { warnings, publicationReadiness, blockingIssues: mergedBlockingIssues } = mergedDraftValidation
  const serverBlockingIssuesNonEmpty = (mergedBlockingIssues ?? []).some(
    (x) => String(x || '').trim() !== ''
  )
  const publishedProyectoId = String(
    payload.draft?.metadata?.publicationResult?.proyectoId ?? ''
  ).trim()
  const alreadyPublishedFromMetadata = publishedProyectoId.length > 0
  const nameSource = String(payload.draft?.metadata?.nameSource || '').toLowerCase()
  const descriptionSourceNorm = String(payload.draft?.metadata?.descriptionSource || '').toLowerCase()
  const showSuggestedNameBadge = nameSource === 'rubro' || nameSource === 'default'
  const showSuggestedDescriptionBadge =
    descriptionSourceNorm === 'rubro' ||
    descriptionSourceNorm === 'default' ||
    descriptionSourceNorm === 'document' ||
    descriptionSourceNorm === 'content' ||
    descriptionSourceNorm === 'extracted'
  const endDateSourceNorm = String(payload.draft?.metadata?.endDateSource || '').toLowerCase()
  const showSuggestedEndDateFromMeta =
    !!endDateSourceNorm && endDateSourceNorm !== 'user' && endDateSourceNorm !== 'manual'
  const showSuggestedEndDateHint =
    showSuggestedEndDateFromMeta || warnings.some((w) => warningMatchesSuggestedEndDate(String(w || '')))
  const showSuggestedNameHint =
    showSuggestedNameBadge || warnings.some((w) => warningMatchesSuggestedName(String(w || '')))
  const showSuggestedDescriptionHint =
    showSuggestedDescriptionBadge ||
    warnings.some((w) => warningMatchesSuggestedDescription(String(w || '')))
  const discardedPastProjectDates = payload.draft?.metadata?.discardedPastProjectDates === true
  const documentationPreview = payload.draft?.metadata?.documentationPreview
  const docPdfUrl = documentationPreview?.primaryStorageUrl?.trim() || ''
  const docPageCount = getEffectiveDocumentationPageCount(
    documentationPreview,
    payload.draft?.metadata
  )

  useEffect(() => {
    setDraftFilesListOpen(false)
  }, [docPdfUrl, metadataFilesWithUrl.length])

  const docPageValidationHint = useMemo(() => {
    if (docPageCount == null) return null
    return validateRubrosDocumentationPages(rubros, docPageCount, metadataFiles)
  }, [docPageCount, rubros, metadataFiles])
  const docStatus = String(documentationPreview?.status || '').trim()
  const docIsPreview = docStatus === 'pending_user_review'
  const showSuggestedDocumentationHint =
    docIsPreview || warnings.some((w) => warningMatchesSuggestedDocumentation(String(w || '')))
  const showGlobalDocBar =
    documentationPreview != null &&
    (docIsPreview || docPageCount != null || !!docPdfUrl)

  const renderDocPrimaryOrFilesControl = useCallback(
    (variant: 'createObra' | 'default') => {
      const linkClass =
        variant === 'createObra' ? styles.createObraRubrosHubStatLink : styles.projectAutofillDocReceivedLink
      const pendingClass =
        variant === 'createObra' ? styles.createObraRubrosHubStatPending : styles.projectAutofillDocReceivedPending
      const popoverClass =
        variant === 'createObra'
          ? styles.draftFilesPopover
          : `${styles.draftFilesPopover} ${styles.draftFilesPopoverAlignEnd}`
      if (docPdfUrl) {
        return (
          <a href={docPdfUrl} target="_blank" rel="noopener noreferrer" className={linkClass}>
            {variant === 'createObra' ? 'Abrir PDF' : 'Ver documentación recibida'}
          </a>
        )
      }
      const n = metadataFilesWithUrl.length
      if (n === 0) {
        return (
          <span className={pendingClass}>
            {variant === 'createObra'
              ? 'Preparando enlace al documento'
              : 'El enlace aparecerá cuando el archivo esté listo'}
          </span>
        )
      }
      if (n === 1) {
        const f = metadataFilesWithUrl[0]
        return (
          <a
            href={String(f.storageUrl).trim()}
            target="_blank"
            rel="noopener noreferrer"
            className={linkClass}
          >
            {variant === 'createObra' ? 'Abrir PDF' : 'Ver documentación recibida'}
          </a>
        )
      }
      return (
        <div className={styles.draftFilesPopoverWrap} ref={draftFilesListRef}>
          <button
            type="button"
            className={
              variant === 'createObra'
                ? styles.draftFilesPopoverTriggerCreateObra
                : styles.draftFilesPopoverTriggerDefault
            }
            aria-expanded={draftFilesListOpen}
            aria-haspopup="dialog"
            aria-label={`Ver ${n} archivos subidos`}
            onClick={() => setDraftFilesListOpen((o) => !o)}
          >
            Ver archivos ({n})
          </button>
          {draftFilesListOpen ? (
            <div className={popoverClass} role="dialog" aria-label="Archivos subidos">
              <button
                type="button"
                className={styles.draftFilesPopoverClose}
                onClick={() => setDraftFilesListOpen(false)}
                aria-label="Cerrar"
              >
                ×
              </button>
              <h4 className={styles.draftFilesPopoverTitle}>Archivos subidos</h4>
              <ul className={styles.draftFilesPopoverList}>
                {metadataFilesWithUrl.map((f, i) => {
                  const k = f.id != null ? String(f.id) : `f-${i}`
                  return (
                    <li key={k}>
                      <a
                        href={String(f.storageUrl).trim()}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={styles.draftFilesPopoverLink}
                      >
                        {draftMetadataFileLabel(f)}
                      </a>
                    </li>
                  )
                })}
              </ul>
            </div>
          ) : null}
        </div>
      )
    },
    [docPdfUrl, draftFilesListOpen, metadataFilesWithUrl]
  )

  const documentExtractionMeta = payload.draft?.metadata?.documentExtraction
  const docExtractParseError = String(documentExtractionMeta?.parseError || '').trim()
  const docExtractOcrError = String(documentExtractionMeta?.ocrError || '').trim()
  const docTechnicalDetail = [docExtractParseError, docExtractOcrError].filter(Boolean).join('\n\n')
  const draftProject = payload.draft?.project
  const subprojects = rubros

  const buildSubprojectsPayload = useCallback(
    (rows: RubroRow[]) =>
      mapRubrosToDraftPayload(rows, (id) => skillOptions.find((s) => s.id === id)?.name),
    [skillOptions]
  )

  const editorValidation = useMemo(
    () =>
      computeProjectAutofillEditorUiState({
        validation: mergedDraftValidation,
        requiredFields,
        requiredFieldsWithZone,
        draftProject,
        name,
        description,
        zone,
        startDate,
        endDate,
        rubros
      }),
    [
      mergedDraftValidation,
      requiredFields,
      requiredFieldsWithZone,
      draftProject,
      name,
      description,
      zone,
      startDate,
      endDate,
      rubros
    ]
  )

  const highlightField = (field: string) => editorValidation.highlightedFields.has(field)

  const today = new Date().toISOString().slice(0, 10)

  const committed = committedRef.current
  const rubrosKeyNow = serializeRubrosSnapshot(rubros)
  const projectDirty =
    !!committed &&
    (name !== committed.name ||
      description !== committed.description ||
      zone !== committed.zone ||
      startDate !== committed.startDate ||
      endDate !== committed.endDate)
  const rubrosDirty = !!committed && rubrosKeyNow !== committed.rubrosKey
  const dirty = projectDirty || rubrosDirty

  const fuzzyNeedsConfirm = rubros.some(
    (r) => isRubroFuzzyKind(r.skillMatchKind) && !fuzzyConfirmByRubro[r.tempId]
  )

  const anyRubroNeedsMasterPdf = useMemo(() => rubros.some(rubroNeedsMasterPdfForCrop), [rubros])

  const highlightedRubroTempIds = useMemo(() => {
    const ids = new Set<string>()
    rubros.forEach((sp, idx) => {
      const k = rubroKey(sp, idx)
      if (sp.manualAdd && !rubroHasValidSelectedSkill(sp)) ids.add(k)
      if (isRubroUnresolvedKind(sp.skillMatchKind) && !sp.selectedSkillId) ids.add(k)
      if (isRubroFuzzyKind(sp.skillMatchKind) && !fuzzyConfirmByRubro[sp.tempId]) ids.add(k)
      if (isRubroFallbackOtrosKind(sp.skillMatchKind)) ids.add(k)
      if (rubroNeedsMasterPdfForCrop(sp) && !docPdfUrl.trim()) ids.add(k)
    })
    if (docPageCount != null) {
      for (const tid of getRubroTempIdsWithDocumentationPageIssues(rubros, docPageCount, metadataFiles)) {
        ids.add(tid)
      }
    }
    const msgMatched = matchValidationMessagesToRubroTempIds(
      [...editorValidation.filteredBlockingIssues, ...editorValidation.warningsNotHandledInline],
      rubros,
      skillOptions
    )
    msgMatched.forEach((tid) => ids.add(tid))
    if (
      editorValidation.highlightedFields.has('subprojects') &&
      rubros.length === 1 &&
      ids.size === 0
    ) {
      ids.add(rubroKey(rubros[0], 0))
    }
    return ids
  }, [
    rubros,
    fuzzyConfirmByRubro,
    docPageCount,
    docPdfUrl,
    metadataFiles,
    editorValidation.filteredBlockingIssues,
    editorValidation.warningsNotHandledInline,
    editorValidation.highlightedFields,
    skillOptions
  ])

  const advisoryNotesByRubroKey = useMemo(() => {
    const out: Record<string, string[]> = {}
    for (const raw of editorValidation.advisoryWarnings) {
      const matched = matchValidationMessagesToRubroTempIds([raw], rubros, skillOptions)
      Array.from(matched).forEach((tid) => {
        if (!out[tid]) out[tid] = []
        if (!out[tid].includes(raw)) out[tid].push(raw)
      })
    }
    return out
  }, [editorValidation.advisoryWarnings, rubros, skillOptions])

  const unmatchedAdvisoryWarnings = useMemo(
    () =>
      editorValidation.advisoryWarnings.filter(
        (raw) => matchValidationMessagesToRubroTempIds([raw], rubros, skillOptions).size === 0
      ),
    [editorValidation.advisoryWarnings, rubros, skillOptions]
  )

  const highlightSubprojectsSection =
    highlightedRubroTempIds.size === 0 &&
    ((rubros.length === 0 && highlightField('subprojects')) ||
      !!docPageValidationHint ||
      (anyRubroNeedsMasterPdf && !docPdfUrl.trim()))

  const {
    isSaving,
    saveState,
    setSaveState,
    isPublishing,
    publishError,
    publishSuccess,
    draftLocked,
    canSaveDraft,
    canPublishDraft,
    handleSave,
    handlePublish,
    applyPatchResponseToValidation
  } = useProjectAutofillSavePublish({
    draftId,
    dirty,
    fuzzyNeedsConfirm,
    docPageValidationHint,
    uploadingSlotKey,
    rubros,
    name,
    description,
    zone,
    startDate,
    endDate,
    editorValidation,
    documentationPreview,
    draftMetadataForPageCount: payload.draft?.metadata,
    anyRubroNeedsMasterPdf,
    docPdfUrl,
    metadataFiles,
    buildSubprojectsPayload,
    patchMetadataPayload,
    setPatchedDraftSync,
    committedRef,
    checkAccess,
    serverBlockingIssuesNonEmpty,
    publicationReadiness,
    alreadyPublishedFromMetadata,
    guardSetApi,
    guardBumpUi,
    edifyUserId
  })

  useEffect(() => {
    if (!isCreateObraShell || !publishSuccess?.proyectoId) return
    if (createObraPublishRedirectRef.current) return
    createObraPublishRedirectRef.current = true
    const rawId = publishSuccess.proyectoId.trim()
    ;(dispatch as (action: unknown) => unknown)(getPrivateProjectById(rawId))
    navigate(`/obrasprivadas/${encodeURIComponent(rawId)}`, {
      replace: true,
      state: { privateProjectJustPublished: true }
    })
  }, [isCreateObraShell, publishSuccess, navigate, dispatch])

  const manualPublishFreeze = isCreateObraShell && isPublishing
  const formFieldsDisabled = draftLocked || manualPublishFreeze

  const { handleSelectRubroSlotFileId, handleRubroSlotPagesChange, handleRubroDocumentationChipRemove, handleUploadRubroSlotFile } =
    useProjectAutofillRubroDocumentation({
      draftId,
      metadataFiles,
      setRubros,
      setMetadataFilesOverride,
      setUploadingSlotKey,
      setSaveState
    })

  const {
    updateRubro,
    applyRubroSkillChange,
    handleRubroSkillSelect,
    handleAddRubro,
    handleRemoveRubro,
    toggleRubroExpanded,
    addRubroBlockedByIncompleteManual
  } = useProjectAutofillRubroInteractions({
    rubros,
    draftLocked,
    startDate,
    endDate,
    skillOptions,
    isCreateObraShell,
    createObraRubroModalRid,
    budgetSheetFullscreenTempId,
    setRubros,
    setExpandedRubros,
    setFuzzyConfirmByRubro,
    setSaveState,
    setCreateObraRubroModalRid,
    setBudgetSheetFullscreenTempId,
    setPendingRubroSkillChange
  })

  useProjectAutofillPayloadHydration({
    payload,
    committedRef,
    setName,
    setDescription,
    setZone,
    setStartDate,
    setEndDate,
    setRubros,
    setFuzzyConfirmByRubro,
    setPatchedDraftSync,
    setExpandedRubros,
    setSaveState
  })

  const patchZoneAfterPlaceSelect = useCallback(
    async (address: string) => {
      if (!draftId || !address.trim()) return
      try {
        const res = await patchProjectAutofillDraft(String(draftId), {
          project: { zone: address.trim() },
          subprojects: buildSubprojectsPayload(rubros),
          metadata: patchMetadataPayload()
        })
        await applyPatchResponseToValidation(res, String(draftId))
      } catch {
      }
    },
    [draftId, rubros, applyPatchResponseToValidation, patchMetadataPayload, buildSubprojectsPayload]
  )

  const budgetSheetFullscreenContext = useMemo(() => {
    if (!budgetSheetFullscreenTempId) return null
    const r = rubros.find((x) => x.tempId === budgetSheetFullscreenTempId)
    if (!r) return null
    const slotsB = normalizeDocumentationSlots(r.documentationSlots ?? {})
    const idsB = normalizeDocumentationFileIds(r.documentationFileIds ?? {})
    const budgetBusy = rubroBudgetSlotHasFileOrPages(slotsB, idsB)
    const readonlyReason = resolveBudgetSheetReadonlyReason(
      {
        tempId: r.tempId,
        detectedLabel: r.detectedLabel,
        selectedSkillId: r.selectedSkillId
      },
      r.budgetSheetState,
      budgetBusy
    )
    return { rubro: r, readonlyReason }
  }, [budgetSheetFullscreenTempId, rubros])

  const createObraDetailModalTarget = useMemo(() => {
    if (!isCreateObraShell || createObraRubroModalRid == null) return null
    const idx = rubros.findIndex((r) => r.tempId === createObraRubroModalRid)
    if (idx < 0) return null
    const rubroRow = rubros[idx]
    return { rubro: rubroRow, rk: rubroKey(rubroRow, idx), rid: createObraRubroModalRid }
  }, [isCreateObraShell, rubros, createObraRubroModalRid])

  useEffect(() => {
    if (!createObraRubroModalRid) return
    if (!rubros.some((r) => r.tempId === createObraRubroModalRid)) {
      setCreateObraRubroModalRid(null)
    }
  }, [rubros, createObraRubroModalRid])

  useEffect(() => {
    if (!budgetSheetFullscreenTempId) return
    if (!rubros.some((r) => r.tempId === budgetSheetFullscreenTempId)) {
      setBudgetSheetFullscreenTempId(null)
    }
  }, [rubros, budgetSheetFullscreenTempId])

  return (
    <div
      className={[
        styles.projectAutofillCard,
        isCreateObraShell ? styles.projectAutofillCardCreateObra : '',
        isPublishing ? styles.projectAutofillCardPublishing : ''
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {payload.documentInsight && !dismissedDocumentInsight ? (
        <DocumentInsightPanel
          insight={payload.documentInsight}
          actionsDisabled={manualPublishFreeze}
          handlers={{
            onUploadTechnicalDoc: onRequestChangeSourceDocument ?? undefined,
            onContinueAnyway: () => setInsightBannerDismissWarnOpen(true)
          }}
        />
      ) : null}
      {!isCreateObraShell && (
        <div className={styles.projectAutofillHeader}>
          <div className={styles.projectAutofillHeaderLead}>
            <span className={styles.projectAutofillTitle}>Borrador de obra</span>
            {publicationReadiness?.readyForCommitSequence && (
              <span
                className={styles.projectAutofillReadyMark}
                title="Listo para publicar"
                aria-label="Listo para publicar"
              >
                <Check size={17} strokeWidth={2.5} aria-hidden />
              </span>
            )}
          </div>
          <span className={styles.projectAutofillStatus}>
            {humanizeDraftStatus(
              mergedCollectedData?.draftStatus ?? payload.draft?.status ?? payload.collectedData?.draftStatus
            )}
          </span>
        </div>
      )}
      {docTechnicalDetail && (
        <div className={styles.projectAutofillDocExtract}>
          <details className={styles.projectAutofillDocExtractDetails}>
            <summary className={styles.projectAutofillDocExtractDetailsSummary}>Detalle técnico</summary>
            <pre className={styles.projectAutofillDocExtractDetailsBody}>{docTechnicalDetail}</pre>
          </details>
        </div>
      )}
      {discardedPastProjectDates && (
        <div className={styles.projectAutofillMetadataNote}>
          Las fechas del documento eran anteriores a hoy; ajustamos el calendario del proyecto a fechas
          vigentes. Revisá inicio y fin.
        </div>
      )}
      {draftLocked && (
        <p className={styles.projectAutofillLockedHint}>Este borrador ya no se puede editar.</p>
      )}
      <div
        className={[
          draftLocked || manualPublishFreeze
            ? styles.projectAutofillDraftLockedWrap
            : embedLayout === 'createObraDesktop'
              ? `${styles.projectAutofillEditorWrap} ${styles.projectAutofillEditorWrapCreateObraSplit}`
              : styles.projectAutofillEditorWrap
        ]
          .filter(Boolean)
          .join(' ')}
      >
      <div
        className={
          isCreateObraShell
            ? styles.projectAutofillCreateObraFormShell
            : styles.projectAutofillEditorOuterPassThrough
        }
      >
        {isCreateObraShell && createObraDetailModalTarget ? (
          <div className={styles.createObraCentralStack}>
            <div className={styles.createObraCentralHead}>
              <h2 className={styles.createObraCentralTitle}>
                {rubroHeadline(createObraDetailModalTarget.rubro, skillOptions)}
              </h2>
              <button
                type="button"
                className={styles.createObraCentralBackBtn}
                disabled={manualPublishFreeze}
                onClick={() => {
                  setBudgetSheetFullscreenTempId(null)
                  setCreateObraRubroModalRid(null)
                }}
              >
                <ChevronLeft size={18} strokeWidth={2} aria-hidden />
                Volver a datos del proyecto
              </button>
            </div>
            <ProjectAutofillRubroDetailBody
              bodyClassName={styles.createObraCentralRubroBody}
              rubro={createObraDetailModalTarget.rubro}
              tempId={createObraDetailModalTarget.rid}
              skillOptions={skillOptions}
              draftLocked={formFieldsDisabled}
              draftId={draftId}
              isSaving={isSaving}
              uploadingSlotKey={uploadingSlotKey}
              documentationPreview={documentationPreview}
              showGlobalDocBar={showGlobalDocBar}
              docPdfUrl={docPdfUrl}
              docPageCount={docPageCount}
              metadataFiles={metadataFiles}
              today={today}
              projectStartDate={startDate}
              projectEndDate={endDate}
              advisoryNotes={advisoryNotesByRubroKey[createObraDetailModalTarget.rk] ?? []}
              handleRubroSkillSelect={handleRubroSkillSelect}
              updateRubro={updateRubro}
              handleSelectRubroSlotFileId={handleSelectRubroSlotFileId}
              handleRubroSlotPagesChange={handleRubroSlotPagesChange}
              handleRubroDocumentationChipRemove={handleRubroDocumentationChipRemove}
              handleUploadRubroSlotFile={handleUploadRubroSlotFile}
              fuzzyConfirmByRubro={fuzzyConfirmByRubro}
              setFuzzyConfirmByRubro={setFuzzyConfirmByRubro}
              rubroUnresolvedNeedsPick={rubroUnresolvedNeedsPick}
              clampIsoDateToMax={clampIsoDateToMax}
              onOpenBudgetSheetFullscreen={setBudgetSheetFullscreenTempId}
            />
          </div>
        ) : (
          <>
            {isCreateObraShell && (
              <div className={styles.projectAutofillCreateObraFormHeader}>
                <h2 className={styles.projectAutofillCreateObraFormTitle}>Datos del proyecto</h2>
                <p className={styles.projectAutofillCreateObraFormSubtitle}>
                  Completá la información para comenzar a recibir cotizaciones
                </p>
              </div>
            )}
      <div className={styles.projectAutofillEditor}>
        <div className={styles.projectAutofillField}>
          <div className={styles.projectAutofillFieldLabelRow}>
            <span className={styles.projectAutofillFieldLabel}>
              {isCreateObraShell ? 'Nombre del Proyecto' : 'Nombre de la obra'}
            </span>
            {showSuggestedNameHint && (
              <span className={styles.projectAutofillFieldSuggested}>Sugerido</span>
            )}
          </div>
          <input
            value={name}
            onChange={(e) => {
              setName(e.target.value)
              setSaveState('idle')
            }}
            placeholder="Ej. Ampliación cocina"
            disabled={formFieldsDisabled}
            aria-invalid={highlightField('project.name') || undefined}
            style={highlightField('project.name') ? PENDING_INPUT_STYLE : undefined}
          />
        </div>
        <div className={`${styles.projectAutofillField} ${styles.projectAutofillPlacesWrap}`}>
          <span className={styles.projectAutofillFieldLabel}>Ubicación de la obra</span>
          <PlacesAutocomplete
            value={zone}
            onChange={(v) => {
              setZone(v)
              setSaveState('idle')
            }}
            onPlaceSelect={({ address }) => {
              patchZoneAfterPlaceSelect(address)
            }}
            placeholder="Ej. Palermo, CABA"
            embed="messageDraft"
            hideLabel
            className={styles.projectAutofillPlaces}
            outlineError={highlightField('project.zone')}
            outlineErrorStyle="draft"
            disabled={formFieldsDisabled}
          />
        </div>
        <div className={styles.projectAutofillField}>
          <span className={styles.projectAutofillFieldLabel}>Fecha de inicio</span>
          <p className={styles.projectAutofillHint}>
            Día en que tu proyecto empieza a publicarse en Edify. Durante ese periodo podes sumar rubros para recibir cotizaciones; no es el inicio de la obra en sitio.
          </p>
          <input
            type="date"
            value={startDate}
            min={today}
            onChange={(e) => {
              const nextStart = e.target.value
              const nextProjEnd = endDate && nextStart && endDate < nextStart ? nextStart : endDate
              setStartDate(nextStart)
              if (endDate && nextStart && endDate < nextStart) {
                setEndDate(nextStart)
              }
              setRubros((prev) =>
                prev.map((r) => {
                  if (r.datesInheritedFromProject === true) {
                    return {
                      ...r,
                      startDate: nextStart,
                      endDate: nextProjEnd || r.endDate
                    }
                  }
                  let s = r.startDate
                  let ed = r.endDate
                  if (nextStart && s && s < nextStart) s = nextStart
                  if (nextStart && ed && ed < nextStart) ed = nextStart
                  if (nextProjEnd) {
                    if (s) s = clampIsoDateToMax(s, nextProjEnd)
                    if (ed) ed = clampIsoDateToMax(ed, nextProjEnd)
                  }
                  if (s && ed && ed < s) ed = s
                  if (s === r.startDate && ed === r.endDate) return r
                  return { ...r, startDate: s, endDate: ed, datesInheritedFromProject: false }
                })
              )
              setSaveState('idle')
            }}
            disabled={formFieldsDisabled}
            aria-invalid={highlightField('project.startDate') || undefined}
            style={highlightField('project.startDate') ? PENDING_INPUT_STYLE : undefined}
          />
        </div>
        <div className={styles.projectAutofillField}>
          <div className={styles.projectAutofillFieldLabelRow}>
            <span className={styles.projectAutofillFieldLabel}>Fecha de finalización</span>
            {showSuggestedEndDateHint && (
              <span className={styles.projectAutofillFieldSuggested}>Sugerido</span>
            )}
          </div>
          <p className={styles.projectAutofillHint}>
            Hasta cuando permanece publicado en Edify. Mientras tanto podes seguir agregando rubros; no representa el fin de la obra en sitio.
          </p>
          <input
            type="date"
            value={endDate}
            min={startDate || today}
            onChange={(e) => {
              const nextEnd = e.target.value
              setEndDate(nextEnd)
              setRubros((prev) =>
                prev.map((r) => {
                  if (r.datesInheritedFromProject === true) {
                    return {
                      ...r,
                      startDate: startDate,
                      endDate: nextEnd
                    }
                  }
                  let s = r.startDate
                  let ed = r.endDate
                  if (nextEnd) {
                    if (s) s = clampIsoDateToMax(s, nextEnd)
                    if (ed) ed = clampIsoDateToMax(ed, nextEnd)
                  }
                  if (s && ed && ed < s) ed = s
                  if (s === r.startDate && ed === r.endDate) return r
                  return { ...r, startDate: s, endDate: ed, datesInheritedFromProject: false }
                })
              )
              setSaveState('idle')
            }}
            disabled={formFieldsDisabled}
            aria-invalid={highlightField('project.endDate') || undefined}
            style={highlightField('project.endDate') ? PENDING_INPUT_STYLE : undefined}
          />
        </div>
        {(payload.draft?.project?.description !== undefined ||
          description.trim() !== '' ||
          requiredFields.includes('project.description')) && (
            <div className={styles.projectAutofillField}>
              <div className={styles.projectAutofillFieldLabelRow}>
                <span className={styles.projectAutofillFieldLabel}>Descripción de la obra</span>
                {showSuggestedDescriptionHint && (
                  <span className={styles.projectAutofillFieldSuggested}>Sugerido</span>
                )}
              </div>
              <textarea
                value={description}
                onChange={(e) => {
                  setDescription(e.target.value)
                  setSaveState('idle')
                }}
                placeholder="Detalle de la obra, materiales, alcance..."
                rows={4}
                disabled={formFieldsDisabled}
                className={styles.projectAutofillProjectDescription}
                aria-invalid={highlightField('project.description') || undefined}
                style={highlightField('project.description') ? PENDING_INPUT_STYLE : undefined}
              />
            </div>
          )}
      </div>
          </>
        )}
      </div>
      <div
        className={[
          styles.projectAutofillSubprojects,
          highlightSubprojectsSection ? styles.projectAutofillSubprojectsHighlight : '',
          embedLayout === 'createObraDesktop' ? styles.projectAutofillSubprojectsCreateObraRail : '',
          isCreateObraShell ? sm.subProjectManager : '',
          isCreateObraShell ? styles.projectAutofillCreateObraRubrosShell : '',
          subprojects.length > 0 ? styles.projectAutofillSubprojectsWithRubros : ''
        ]
          .filter(Boolean)
          .join(' ')}
        aria-invalid={highlightSubprojectsSection || undefined}
      >
        <div
          className={
            isCreateObraShell
              ? styles.projectAutofillCreateObraRailColumn
              : styles.projectAutofillSubprojectsPassThrough
          }
        >
          <div
            className={
              isCreateObraShell
                ? styles.projectAutofillCreateObraRailScroll
                : styles.projectAutofillSubprojectsPassThrough
            }
          >
        {isCreateObraShell ? (
          <div className={styles.createObraRubrosHub}>
            <div className={styles.createObraRubrosHubIntro}>
              <div className={sm.headerMain}>
                <div className={sm.headerText}>
                  <h3 className={sm.title}>Rubros a cotizar</h3>
                  <p className={sm.subtitle}>Agregá los trabajos que necesitás cotizar</p>
                </div>
                {showGlobalDocBar ? (
                  <div className={sm.infoButtonWrap} ref={createObraPdfInfoRef}>
                    <button
                      type="button"
                      className={sm.infoButton}
                      aria-expanded={createObraPdfInfoOpen}
                      aria-haspopup="dialog"
                      aria-label="Información sobre el documento analizado"
                      disabled={manualPublishFreeze}
                      onClick={() => setCreateObraPdfInfoOpen((o) => !o)}
                    >
                      <Info size={16} aria-hidden />
                    </button>
                    {createObraPdfInfoOpen ? (
                      <div
                        className={sm.infoPopover}
                        role="dialog"
                        aria-label="Documento analizado"
                      >
                        <button
                          type="button"
                          className={sm.infoPopoverClose}
                          onClick={() => setCreateObraPdfInfoOpen(false)}
                          aria-label="Cerrar"
                        >
                          ×
                        </button>
                        <h4 className={sm.infoPopoverTitle}>Documento analizado</h4>
                        <p className={sm.infoPopoverIntro}>
                          El PDF analizado es la base de este borrador: sumá rubros manualmente o reemplazá el
                          documento para volver a detectarlos automáticamente.
                        </p>
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </div>
            </div>
            {subprojects.length === 0 && !draftLocked && !manualPublishFreeze && (
              <p className={styles.createObraRubrosHubCallout}>
                Todavía no hay rubros en el proyecto. Usá «Agregar rubro» en la lista o, si acabás de subir un
                documento, esperá a que termine el análisis.
              </p>
            )}
            {showGlobalDocBar ? (
              <div className={styles.createObraRubrosHubStatus} role="status">
                <p className={styles.createObraRubrosHubStatComment}>
                  {docPageCount != null
                    ? docPageCount === 1
                      ? '1 página relevada en el documento'
                      : `${docPageCount} páginas relevadas en el documento`
                    : 'Documento procesado'}
                </p>
                <div className={styles.createObraRubrosHubStatusRow}>
                  <div className={styles.createObraRubrosHubStatusLinksLeft}>
                    {renderDocPrimaryOrFilesControl('createObra')}
                  </div>
                  {!draftLocked && !manualPublishFreeze && draftId && onRequestChangeSourceDocument ? (
                    <button
                      type="button"
                      className={styles.createObraRubrosHubStatusDocAction}
                      onClick={onRequestChangeSourceDocument}
                    >
                      <FileUp size={15} strokeWidth={1.75} aria-hidden />
                      Cambiar documentación
                    </button>
                  ) : null}
                </div>
                {!draftLocked && !manualPublishFreeze && draftId && !onRequestChangeSourceDocument ? (
                  <p className={styles.createObraRubrosHubStatusDocHint}>
                    Para analizar otro PDF, volvé al inicio del asistente.
                  </p>
                ) : null}
              </div>
            ) : null}
          </div>
        ) : (
          <div className={styles.projectAutofillSubprojectsToolbar}>
            <span className={styles.projectAutofillSubprojectsTitle}>
              Rubros a cotizar en la obra ({subprojects.length})
            </span>
            {!draftLocked && (
              <button
                type="button"
                className={styles.projectAutofillAddRubroButton}
                onClick={handleAddRubro}
                disabled={addRubroBlockedByIncompleteManual || isSaving || !draftId}
                title={
                  addRubroBlockedByIncompleteManual
                    ? 'Elegí el rubro de la lista en cada ítem agregado antes de sumar otro.'
                    : undefined
                }
              >
                <Plus size={16} />
                Agregar rubro
              </button>
            )}
          </div>
        )}
        {unmatchedAdvisoryWarnings.map((text, i) => (
          <p key={`adv-${i}`} className={styles.projectAutofillRubroAdvisoryNote} role="note">
            <span className={styles.projectAutofillRubroAdvisoryBadge}>Revisar</span>
            {text}
          </p>
        ))}
        {draftLocked && (
          <p className={styles.projectAutofillRubrosLockedHint}>{PUBLISHED_RUBRO_EDIT_HINT}</p>
        )}
        {!isCreateObraShell && subprojects.length === 0 && !draftLocked && (
          <p className={styles.projectAutofillRubrosEmptyHint}>
            No hay rubros cargados. Agregá al menos uno para guardar el borrador y publicar la obra.
          </p>
        )}
        {showGlobalDocBar && !isCreateObraShell ? (
          <div className={styles.projectAutofillDocReceived}>
            <div className={styles.projectAutofillDocReceivedRow}>
              <div className={styles.projectAutofillDocReceivedTitleGroup}>
                <span className={styles.projectAutofillDocReceivedTitle}>Documentación recibida</span>
                {showSuggestedDocumentationHint && (
                  <span className={styles.projectAutofillFieldSuggested}>Sugerido</span>
                )}
              </div>
              {renderDocPrimaryOrFilesControl('default')}
            </div>
            {(() => {
              const metaParts: string[] = []
              if (docPageCount != null) {
                metaParts.push(
                  docPageCount === 1
                    ? '1 página analizada'
                    : `${docPageCount} páginas analizadas`
                )
              }
              if (metaParts.length === 0) return null
              return (
                <p className={styles.projectAutofillDocReceivedMeta}>{metaParts.join('. ')}</p>
              )
            })()}
            <p className={styles.projectAutofillDocReceivedRollback}>
              ¿No es el documento correcto? Enviá otro archivo en el chat del agente para analizarlo de nuevo y
              actualizar esta propuesta.
            </p>
          </div>
        ) : null}
        {(isCreateObraShell ? (
          <div
            className={[
              sm.subProjectsListScroll,
              styles.projectAutofillCreateObraSubProjectsListScroll
            ]
              .filter(Boolean)
              .join(' ')}
          >
            <div className={sm.subProjectsList}>
              <div className={sm.subProjectsListInner}>
                <div className={styles.createObraRubrosListToolbar}>
                  <span className={styles.createObraRubrosListCounter} aria-live="polite">
                    {subprojects.length} rubro{subprojects.length !== 1 ? 's' : ''}
                  </span>
                  {!draftLocked && !manualPublishFreeze && draftId ? (
                    <button
                      type="button"
                      className={[sm.addButton, styles.createObraRubrosListToolbarAdd].join(' ')}
                      onClick={handleAddRubro}
                      disabled={isSaving || addRubroBlockedByIncompleteManual || manualPublishFreeze}
                      title={
                        addRubroBlockedByIncompleteManual
                          ? 'Elegí el rubro de la lista en cada ítem agregado antes de sumar otro.'
                          : undefined
                      }
                    >
                      <Plus size={18} />
                      Agregar rubro
                    </button>
                  ) : null}
                </div>
                {subprojects.map((sp, idx) => {
                  const rk = rubroKey(sp, idx)
                  const rid = sp.tempId || `sp-${idx}`
                  const isEditingInCentral = createObraRubroModalRid === rid
                  return (
                    <div
                      key={rk}
                      className={[
                        sm.subProjectItem,
                        highlightedRubroTempIds.has(rk) ? sm.invalid : '',
                        isEditingInCentral ? styles.createObraRubroListItemEditing : ''
                      ]
                        .filter(Boolean)
                        .join(' ')}
                    >
                      <div className={sm.subProjectMain}>
                        <div className={sm.subProjectContent}>
                          <div className={sm.subProjectHeader}>
                            {rubroUnresolvedNeedsPick(sp) ? (
                              <div
                                className={styles.projectAutofillSubprojectLabelBlock}
                                style={{ flex: 1, minWidth: 0 }}
                              >
                                <span className={styles.projectAutofillRubroUnresolvedTitle}>
                                  Se detectó un rubro en el documento
                                </span>
                                <span className={styles.projectAutofillRubroUnresolvedLine}>
                                  No encontramos el nombre correcto en la plataforma.
                                </span>
                                <span className={styles.projectAutofillRubroUnresolvedLineMuted}>
                                  Elegí el rubro en la lista.
                                </span>
                              </div>
                            ) : rubroFallbackOtrosReview(sp) ? (
                              <div
                                className={styles.projectAutofillSubprojectLabelBlock}
                                style={{ flex: 1, minWidth: 0 }}
                              >
                                <span className={styles.projectAutofillRubroUnresolvedTitle}>
                                  Revisá el rubro
                                </span>
                                <span className={styles.projectAutofillRubroUnresolvedLine}>
                                  La coincidencia con un rubro de la plataforma no fue clara; se asignó un valor por
                                  defecto.
                                </span>
                                <span className={styles.projectAutofillRubroUnresolvedLineMuted}>
                                  Cambiá el rubro en la lista si corresponde.
                                </span>
                              </div>
                            ) : (
                              <h4 className={sm.subProjectName}>{rubroHeadline(sp, skillOptions)}</h4>
                            )}
                            <div className={sm.subProjectActions}>
                              <button
                                type="button"
                                className={sm.actionButton}
                                onClick={() => setCreateObraRubroModalRid(rid)}
                                disabled={!draftId || isSaving || formFieldsDisabled}
                                title={draftLocked ? PUBLISHED_RUBRO_EDIT_HINT : 'Editar rubro'}
                              >
                                <Edit3 size={16} />
                              </button>
                              <button
                                type="button"
                                className={`${sm.actionButton} ${sm.deleteButton}`}
                                onClick={() => handleRemoveRubro(rid)}
                                disabled={!draftId || isSaving || formFieldsDisabled}
                                title={draftLocked ? PUBLISHED_RUBRO_EDIT_HINT : 'Eliminar rubro'}
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          </div>
        ) : (
          subprojects.map((sp, idx) => {
            const rk = rubroKey(sp, idx)
            const rid = sp.tempId || `sp-${idx}`
            return (
              <ProjectAutofillDefaultRubroCard
                key={rk}
                rk={rk}
                rid={rid}
                idx={idx}
                sp={sp}
                highlighted={highlightedRubroTempIds.has(rk)}
                expanded={!!expandedRubros[rid]}
                headline={rubroHeadline(sp, skillOptions)}
                advisoryNotes={advisoryNotesByRubroKey[rk] ?? []}
                skillOptions={skillOptions}
                draftLocked={draftLocked}
                draftId={draftId}
                isSaving={isSaving}
                projectStartDate={startDate}
                projectEndDate={endDate}
                today={today}
                documentationPreview={documentationPreview}
                metadataFiles={metadataFiles}
                showGlobalDocBar={showGlobalDocBar}
                docPdfUrl={docPdfUrl}
                docPageCount={docPageCount}
                uploadingSlotKey={uploadingSlotKey}
                fuzzyConfirmByRubro={fuzzyConfirmByRubro}
                onToggleExpand={toggleRubroExpanded}
                onRemoveRubro={handleRemoveRubro}
                onRubroSkillSelect={handleRubroSkillSelect}
                onUpdateRubro={updateRubro}
                onSelectRubroSlotFileId={handleSelectRubroSlotFileId}
                onRubroSlotPagesChange={handleRubroSlotPagesChange}
                onRubroDocumentationChipRemove={handleRubroDocumentationChipRemove}
                onUploadRubroSlotFile={handleUploadRubroSlotFile}
                onOpenBudgetSheetFullscreen={setBudgetSheetFullscreenTempId}
                onFuzzyConfirmChange={(tempId, checked) =>
                  setFuzzyConfirmByRubro((prev) => ({ ...prev, [tempId]: checked }))
                }
              />
            )
          })
        )
        )}
          </div>
          {isCreateObraShell ? (
            <ProjectAutofillSaveFooter
              draftId={draftId}
              dirty={dirty}
              saveState={saveState}
              canSaveDraft={canSaveDraft}
              isSaving={isSaving}
              publishSuccess={publishSuccess}
              alreadyPublishedFromMetadata={alreadyPublishedFromMetadata}
              publishedProyectoId={publishedProyectoId}
              canPublishDraft={canPublishDraft}
              isPublishing={isPublishing}
              publishError={publishError}
              onSave={handleSave}
              onPublish={handlePublish}
              rail
            />
          ) : null}
        </div>
      </div>
      </div>
      <DocumentInsightProceedWarnModal
        open={insightBannerDismissWarnOpen}
        mode="dismissInsightBanner"
        busy={manualPublishFreeze}
        onCancel={() => setInsightBannerDismissWarnOpen(false)}
        onConfirm={() => {
          setInsightBannerDismissWarnOpen(false)
          setDismissedDocumentInsight(true)
        }}
      />
      <BudgetSheetFullscreenModal
        isOpen={budgetSheetFullscreenContext != null}
        onClose={() => setBudgetSheetFullscreenTempId(null)}
        rubroTitle={
          budgetSheetFullscreenContext
            ? rubroPrimaryDisplayName(budgetSheetFullscreenContext.rubro, skillOptions)
            : ''
        }
        state={budgetSheetFullscreenContext?.rubro.budgetSheetState}
        onChange={(next) => {
          const tid = budgetSheetFullscreenContext?.rubro.tempId
          if (tid) updateRubro(tid, { budgetSheetState: next })
        }}
        readonlyReason={budgetSheetFullscreenContext?.readonlyReason ?? 'none'}
        disabled={isSaving || !draftId || manualPublishFreeze}
      />
      <ModalOverlay
        isOpen={!!pendingRubroSkillChange}
        onClose={() => setPendingRubroSkillChange(null)}
        zIndex={10}
      >
        <div className={styles.projectAutofillConfirmDialog}>
          <div className={styles.projectAutofillConfirmDialogHeader}>
            <h2 className={styles.projectAutofillConfirmDialogTitle}>Cambiar rubro</h2>
            <button
              type="button"
              className={styles.projectAutofillConfirmDialogClose}
              onClick={() => setPendingRubroSkillChange(null)}
              aria-label="Cerrar"
            >
              <X size={20} strokeWidth={2} aria-hidden />
            </button>
          </div>
          <p className={styles.projectAutofillConfirmDialogText}>
            Podés mantener la documentación vinculada (páginas sugeridas del documento y archivos cargados
            por tipo) o quitarla por completo.
          </p>
          <div className={styles.projectAutofillConfirmDialogActions}>
            <button
              type="button"
              className={styles.projectAutofillConfirmDialogClearDoc}
              disabled={manualPublishFreeze}
              onClick={() => {
                const p = pendingRubroSkillChange
                if (!p) return
                applyRubroSkillChange(p.tempId, p.skill, true)
                setPendingRubroSkillChange(null)
              }}
            >
              Cambiar rubro y quitar documentación
            </button>
            <button
              type="button"
              className={styles.projectAutofillConfirmDialogPrimary}
              disabled={manualPublishFreeze}
              onClick={() => {
                const p = pendingRubroSkillChange
                if (!p) return
                applyRubroSkillChange(p.tempId, p.skill, false)
                setPendingRubroSkillChange(null)
              }}
            >
              Cambiar rubro y mantener documentación
            </button>
          </div>
        </div>
      </ModalOverlay>
      {!isCreateObraShell ? (
        <ProjectAutofillSaveFooter
          draftId={draftId}
          dirty={dirty}
          saveState={saveState}
          canSaveDraft={canSaveDraft}
          isSaving={isSaving}
          publishSuccess={publishSuccess}
          alreadyPublishedFromMetadata={alreadyPublishedFromMetadata}
          publishedProyectoId={publishedProyectoId}
          canPublishDraft={canPublishDraft}
          isPublishing={isPublishing}
          publishError={publishError}
          onSave={handleSave}
          onPublish={handlePublish}
        />
      ) : null}
      {isPublishing ? (
        <div
          className={styles.projectAutofillPublishingOverlay}
          role="status"
          aria-live="polite"
          aria-busy="true"
        >
          <div className={styles.projectAutofillPublishingOverlayPanel}>
            <Loader2
              className={styles.projectAutofillPublishingSpinner}
              size={48}
              strokeWidth={2}
              aria-hidden
            />
            <p className={styles.projectAutofillPublishingTitle}>Publicando la obra…</p>
            <p className={styles.projectAutofillPublishingHint}>Esto puede tardar unos segundos.</p>
          </div>
        </div>
      ) : null}
    </div>
  )
}

export default ProjectAutofillCard
